import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Settings as SettingsIcon, Database, Key, Shield, Bell, Tag } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { safeLocalStorage, safeParseJson } from "../lib/storage.js";
import { usePushNotifications } from "../hooks/usePushNotifications.js";

// Reusable tab configuration
export const createTabItems = (items) => {
  return items.map((item) => (
    <TabsTrigger 
      key={item.value}
      value={item.value} 
      className="rounded-md px-4 py-2 data-[state=active]:bg-purple-600 data-[state=active]:text-white transition-all"
    >
      {item.label}
    </TabsTrigger>
  ));
};

export default function Settings() {
  // Add state for badges
  const [badges, setBadges] = useState([]);
  const [newBadge, setNewBadge] = useState({ name: "", color: "#FF5733" });

  // Helper function to determine if a color is light or dark
  const isLightColor = (hexColor) => {
    // Convert hex to RGB
    const r = parseInt(hexColor.slice(1, 3), 16);
    const g = parseInt(hexColor.slice(3, 5), 16);
    const b = parseInt(hexColor.slice(5, 7), 16);
    
    // Calculate luminance (perceived brightness)
    // Using the formula: 0.299*R + 0.587*G + 0.114*B
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    
    // Return true if the color is light (luminance > 0.5)
    return luminance > 0.5;
  };

  const { isSupported, isSecureContext, permission, subscription, subscribe, unsubscribe, error } = usePushNotifications();

  // Tab configuration
  const settingsTabs = [
    { value: "general", label: "General" },
    { value: "notificaciones", label: "Notificaciones" },
    { value: "badges", label: "Badges" },
    { value: "advanced", label: "Avanzado" }
  ];

  // Load badges from localStorage on component mount
  useEffect(() => {
    const storedBadges = safeParseJson(safeLocalStorage.getItem("planBadges"), []);
    setBadges(storedBadges);
  }, []);

  // Save badges to localStorage whenever they change
  useEffect(() => {
    safeLocalStorage.setItem("planBadges", JSON.stringify(badges));
  }, [badges]);

  // Handle adding a new badge
  const handleAddBadge = () => {
    if (newBadge.name.trim()) {
      setBadges([...badges, { id: Date.now().toString(), ...newBadge }]);
      setNewBadge({ name: "", color: "#FF5733" });
    }
  };

  // Handle removing a badge
  const handleRemoveBadge = (id) => {
    setBadges(badges.filter(badge => badge.id !== id));
  };

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Configuración</h1>
        <p className="text-gray-600">Administra la configuración de la aplicación</p>
      </div>

      <Tabs defaultValue="general" className="w-full">
        <TabsList className="flex mb-4 bg-gray-100 p-1 rounded-lg">
          {createTabItems(settingsTabs)}
        </TabsList>
        
        <TabsContent value="general">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <SettingsIcon className="mr-2 h-5 w-5" />
                Configuración General
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-500">
                Configuración general de la aplicación.
              </p>
              {/* Existing general settings content */}
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="badges">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Tag className="mr-2 h-5 w-5" />
                Configuración de Badges
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <p className="text-sm text-gray-500">
                  Configura los badges que se pueden usar en los planes.
                </p>
                
                {/* Form to add new badge */}
                <div className="flex items-end gap-4 pb-4 border-b">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nombre del Badge
                    </label>
                    <Input
                      type="text"
                      value={newBadge.name}
                      onChange={(e) => setNewBadge({...newBadge, name: e.target.value})}
                      placeholder="Ej: Popular, Nuevo, etc."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Color
                    </label>
                    <Input
                      type="color"
                      value={newBadge.color}
                      onChange={(e) => setNewBadge({...newBadge, color: e.target.value})}
                      className="h-10 w-20"
                    />
                  </div>
                  <Button onClick={handleAddBadge}>Agregar</Button>
                </div>
                
                {/* List of existing badges */}
                <div className="space-y-2">
                  <h3 className="text-lg font-medium">Badges Existentes</h3>
                  {badges.length === 0 ? (
                    <p className="text-sm text-gray-500">No hay badges configurados.</p>
                  ) : (
                    <ul className="space-y-2">
                      {badges.map(badge => (
                        <li key={badge.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-md">
                          <div className="flex items-center">
                            <span 
                              className="inline-block w-4 h-4 rounded-full mr-2" 
                              style={{ backgroundColor: badge.color }}
                            ></span>
                            <span>{badge.name}</span>
                          </div>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => handleRemoveBadge(badge.id)}
                            className="text-red-500 hover:text-red-700"
                          >
                            Eliminar
                          </Button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                
                {/* CSS Code for Badges */}
                <div className="mt-8 border rounded-md p-4 bg-gray-50">
                  <h3 className="text-lg font-medium mb-2">Código CSS para Badges</h3>
                  <p className="text-sm text-gray-500 mb-4">
                    Copia y pega este código CSS para usar los badges en tu aplicación.
                  </p>
                  
                  {badges.length === 0 ? (
                    <p className="text-sm text-gray-500">No hay badges configurados para generar CSS.</p>
                  ) : (
                    <div className="relative">
                      <pre className="bg-gray-900 text-gray-100 p-4 rounded-md overflow-x-auto text-sm">
                        <code>
                          {badges.map(badge => 
                            `.badge-${badge.name.toLowerCase().replace(/\s+/g, '-')} {
  background-color: ${badge.color};
  color: ${isLightColor(badge.color) ? '#000000' : '#ffffff'};
}
`
                          ).join('\n')}
                        </code>
                      </pre>
                      <Button 
                        className="absolute top-2 right-2 bg-gray-800 hover:bg-gray-700 text-white"
                        size="sm"
                        onClick={() => {
                          const cssCode = badges.map(badge => 
                            `.badge-${badge.name.toLowerCase().replace(/\s+/g, '-')} {
  background-color: ${badge.color};
  color: ${isLightColor(badge.color) ? '#000000' : '#ffffff'};
}`
                          ).join('\n\n');
                          try {
                            navigator.clipboard.writeText(cssCode);
                            alert('CSS copiado al portapapeles');
                          } catch (e) {
                            alert('No se pudo copiar el CSS');
                          }
                        }}
                      >
                        Copiar
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="notificaciones">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Bell className="mr-2 h-5 w-5" />
                Notificaciones Push
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {!isSupported ? (
                <p className="text-sm text-red-500">Tu navegador no soporta notificaciones push.</p>
              ) : !isSecureContext ? (
                <p className="text-sm text-red-500">Requiere HTTPS (o localhost) para activar notificaciones push.</p>
              ) : (
                <>
                  <p className="text-sm text-gray-600">
                    Recibe alertas en tu navegador cuando un usuario <strong>contrate</strong>,
                    se <strong>suscriba al método de pago</strong> o se <strong>emita un certificado</strong>.
                  </p>
                  {error ? (
                    <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                      {String(error)}
                    </div>
                  ) : null}
                  <div className="flex items-center gap-3">
                    <span className={`text-sm font-medium ${subscription ? 'text-[var(--yool-success)]' : 'text-gray-500'}`}>
                      {subscription ? '● Activadas' : '○ Desactivadas'}
                    </span>
                    {permission === 'denied' && (
                      <span className="text-xs text-red-500">Permiso denegado en el navegador — habilítalo en Configuración del sitio.</span>
                    )}
                  </div>
                  {!subscription ? (
                    <Button onClick={subscribe} disabled={permission === 'denied'} className="bg-purple-600 hover:bg-purple-700">
                      <Bell className="mr-2 h-4 w-4" /> Activar notificaciones
                    </Button>
                  ) : (
                    <Button onClick={unsubscribe} variant="outline">
                      Desactivar notificaciones
                    </Button>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="advanced">
          <Card>
            <CardHeader>
              <CardTitle>Configuración Avanzada</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-500">
                Configuración avanzada de la aplicación.
              </p>
              {/* Existing advanced settings content */}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
